import { forwardRef } from "react";
import type { ButtonHTMLAttributes, ReactNode } from "react";
import { classNames } from "../internal/classNames";
import "./Button.css";

export type ButtonVariant = "primary" | "secondary" | "quiet" | "danger";
export type ButtonSize = "small" | "medium" | "large";

interface ButtonBaseProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "disabled"> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  isLoading?: boolean;
  loadingLabel?: string;
  disabled?: boolean;
  leadingIcon?: ReactNode;
  trailingIcon?: ReactNode;
}

interface TextButtonProps extends ButtonBaseProps {
  iconOnly?: false;
  children: ReactNode;
}

interface IconButtonProps extends ButtonBaseProps {
  iconOnly: true;
  children: ReactNode;
  "aria-label": string;
}

export type ButtonProps = TextButtonProps | IconButtonProps;

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = "primary",
    size = "medium",
    isLoading = false,
    loadingLabel = "처리 중",
    disabled = false,
    iconOnly = false,
    leadingIcon,
    trailingIcon,
    className,
    children,
    type = "button",
    ...props
  },
  ref,
) {
  const ariaLabel = props["aria-label"];

  return (
    <button
      {...props}
      ref={ref}
      type={type}
      aria-busy={isLoading || undefined}
      aria-label={isLoading ? loadingLabel : ariaLabel}
      className={classNames(
        "cp-button",
        `cp-button--${variant}`,
        `cp-button--${size}`,
        iconOnly && "cp-button--icon-only",
        className,
      )}
      data-loading={isLoading || undefined}
      disabled={disabled || isLoading}
    >
      {isLoading ? <span aria-hidden="true" className="cp-button__spinner" /> : leadingIcon}
      {iconOnly && !isLoading ? children : !iconOnly && <span className="cp-button__label">{children}</span>}
      {!iconOnly && !isLoading && trailingIcon}
    </button>
  );
});
